import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WebSocketProvider } from './context/WebSocketContext';
import Login from './components/auth/LoginPage';
import Sidebar from './components/common/Sidebar';
import Dashboard from './components/dashboard/Dashboard';
import Employees from './components/employees/EmployeeList';
import DepartmentManagement from './components/departments/DepartmentManagement';
import LeaveManagement from './components/leave/LeaveManagement';
import DocumentCenter from './components/documentCenter/DocumentCenter';
import EmployeeSelfService from './components/employeeSelfService/EmployeeSelfService';
import RequestSystem from './components/requestSystem/RequestSystem';
import Reports from './components/reports/Reports';
import Notifications from './components/notifications/Notifications';
import NotificationPreferences from './components/notifications/NotificationPreferences';
import PayrollManagement from './components/payroll/PayrollManagement';
import PerformanceManagement from './components/performance/Performance';
import Settings from './components/settings/Settings';
import Kanban from './components/tasks/Kanban';
import Projects from './components/tasks/Projects';
import ProjectDetail from './components/tasks/ProjectDetail';
import RolesPermissions from './components/admin/RolesPermissions';
import UsersManagement from './components/admin/UsersManagement';
import DocumentCategories from './components/documentCenter/DocumentCategories';
import RequestAdmin from './components/requestSystem/RequestAdmin';
import NotificationBadge from './components/common/NotificationBadge';
import ActivityFeed from './components/common/ActivityFeed';
import StatusIndicator from './components/common/StatusIndicator';
import ChatWidget from './components/common/ChatWidget';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  console.log('🔍 ProtectedRoute - isAuthenticated:', isAuthenticated, 'loading:', loading);

  if (loading) {
    console.log('🔍 ProtectedRoute - Showing loading spinner');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  console.log('🔍 ProtectedRoute - isAuthenticated:', isAuthenticated);
  
  if (!isAuthenticated) {
    console.log('🔍 ProtectedRoute - Not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }

  console.log('🔍 ProtectedRoute - Authenticated, rendering children');
  return children;
};

// Main App Layout
const MainApp = () => {
  const { user } = useAuth();
  console.log('🔍 MainApp - User:', user);

  return (
    <WebSocketProvider>
      <Sidebar>
        <Routes>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/employees" element={<Employees />} />
          <Route path="/departments" element={<DepartmentManagement />} />
          <Route path="/leave" element={<LeaveManagement user={user} />} />
          <Route path="/documents" element={<DocumentCenter />} />
          <Route path="/self-service" element={<EmployeeSelfService />} />
          <Route path="/requests" element={<RequestSystem />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/notifications/preferences" element={<NotificationPreferences />} />
          <Route path="/payroll" element={<PayrollManagement />} />
          <Route path="/tasks" element={<Kanban />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/projects/:id" element={<ProjectDetail />} />
          <Route path="/performance" element={<PerformanceManagement />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/admin/roles-permissions" element={<RolesPermissions />} />
          <Route path="/admin/users" element={<UsersManagement />} />
          <Route path="/documents/categories" element={<DocumentCategories />} />
          <Route path="/requests/admin" element={<RequestAdmin />} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        
        {/* Real-time Components - Fixed Top-Right Position */}
        <div className="fixed top-4 right-4 z-50 flex flex-col items-end space-y-2">
          <div className="flex flex-col md:flex-row items-end space-y-2 md:space-y-0 md:space-x-2">
            <NotificationBadge />
            <ActivityFeed />
            <StatusIndicator />
          </div>
          <ChatWidget />
        </div>
      </Sidebar>
    </WebSocketProvider>
  );
};

const App = () => {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <MainApp />
            </ProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </Router>
  );
};

export default App; 